import type { Person, PersonService } from '@/store/people/people.types';

/**
 * Who may do what to a vessel's plan.
 *
 * Answered from the caller's role row on that one vessel rather than from their role name.
 * The rights are what the People service actually gates the endpoints with
 * (`server/server.go`), and they are no longer the owner's alone: a captain can be the
 * vessel's payer, so `1788520044_grant_owner_permissions_to_captain` hands the captain
 * `GetSubscription`, `EditSubscription` and `SeatManagement` too — and an owner can grant or
 * withdraw them per vessel, which a role name cannot express either way.
 *
 * Every seat carries all three (`1785913309_add_subscription_permissions_to_seats`), so a
 * captain on a harbor seat still holds them: the role permissions are the real gate.
 */

/** `GET /subscriptions` — the plan is readable. */
const GET_SUBSCRIPTION = 'GetSubscription';
/** `POST /subscriptions/checkout`, `PATCH|cancel|uncancel /subscriptions/{id}`. */
const EDIT_SUBSCRIPTION = 'EditSubscription';
/** `PATCH|DELETE /roles/seat` — handing the plan's seats out. */
const SEAT_MANAGEMENT = 'SeatManagement';

const VESSEL = 'vessel';

/** The person's role row on one service, or `undefined` if they hold none. */
export const membershipOf = (
  person: Person | undefined,
  serviceId: string,
): PersonService | undefined => person?.services.find((service) => service.serviceId === serviceId);

/**
 * Whether the plan may be changed, bought, cancelled or resumed. Implies reading it — the
 * endpoints that write are gated on this right alone.
 */
export const canEditPlan = (service: PersonService | undefined): boolean =>
  !!service?.rights.includes(EDIT_SUBSCRIPTION);

/**
 * Whether the plan may be read at all. The two rights are granted together by default, but an
 * owner may withdraw the edit half from a role, which leaves the plan visible and frozen.
 */
export const canViewPlan = (service: PersonService | undefined): boolean =>
  !!service?.rights.includes(GET_SUBSCRIPTION) || canEditPlan(service);

/** Whether the plan's seats may be handed out or taken back. */
export const canManageSeats = (service: PersonService | undefined): boolean =>
  !!service?.rights.includes(SEAT_MANAGEMENT);

/**
 * The vessels whose plan this person may at least see — what the account list is built from.
 *
 * Vessel services only: a service-provider account has people and roles of its own but no
 * plan behind them, and asking the vessel service for one by id would answer nothing.
 */
export const planManageableServices = (person: Person | undefined): PersonService[] =>
  (person?.services ?? []).filter(
    (service) => service.serviceType === VESSEL && canViewPlan(service),
  );
